import type { Media, MultilingualLabel } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import { useEffect } from 'react';
import { UseFormReset, UseFormWatch } from 'react-hook-form';

import { useAppDispatch } from 'Libs/redux/store';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import { fetchBreakingNewsById, updateBreakingNews } from 'Store/entities/activity/activity.thunks';
import { ActivityAudience, BreakingNews } from 'Store/entities/activity/activity.type';

import { type AudienceFormType, type AudienceFormValues, saveAudience } from '../AudienceForm/audience.utils';
import { dateToString, formatNotificationTime } from './libs/dateTimeUtils';

export type BreakingNewsFormValues = {
  coverMedia?: Media;
  name?: Partial<MultilingualLabel>;
  text?: Partial<MultilingualLabel>;
  description?: Partial<MultilingualLabel>;
  isLongText?: boolean;
  isRecurringNotification: boolean;
  isNotificationActive: boolean;
  notificationTime: Date;
  startDate?: Date;
  endDate?: Date;
} & AudienceFormType & { audienceDirty: boolean };

type UseBreakingNewsFormProps = {
  breakingNews: BreakingNews;
  watch: UseFormWatch<BreakingNewsFormValues>;
  reset: UseFormReset<BreakingNewsFormValues>;
  defaultValues: BreakingNewsFormValues;
  setVisibleSaveModal: (visible: boolean) => void;
  isDirty: boolean;
};

const ALERT_DURATION = 5000;

const getErrorText = (error: any) => {
  if (error?.errorCode === 'AUD-CMC-CMC-001') {
    return t('api-errors:magic_code_already_exists');
  }

  if (JSON.stringify(error?.params)?.includes('audience')) {
    return t('api-errors:missing_audience');
  }

  return t('breaking_news:edition.update_fail');
};

export const useSubmitBreakingNewsForm = ({
  breakingNews,
  watch,
  reset,
  isDirty,
  defaultValues,
  setVisibleSaveModal,
}: UseBreakingNewsFormProps) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    reset({ ...defaultValues });
  }, [breakingNews]);

  const handleBreakingNewsAudienceSave = async (
    audiencesFormValues: AudienceFormValues,
    hasAudienceChanged: boolean,
    currentAudience?: ActivityAudience[],
  ) => {
    if (!audiencesFormValues) return;

    await saveAudience({
      hasAudienceChanged,
      audienceFormValues: audiencesFormValues,
      activityAudience: currentAudience,
      entity: {
        id: breakingNews.id,
        type: 'activity',
      },
      dispatch,
    });
    dispatch(fetchBreakingNewsById(breakingNews.id));
    setVisibleSaveModal(false);
  };

  const handleSaveBreakingNews = async () => {
    const formValues = watch();
    const audienceDirty = watch('audienceDirty');

    const { audiences, ...restOfBreakingNewsValues } = breakingNews;

    const { unwrap: unwrapUpdateBreakingNews } = dispatch(
      updateBreakingNews({
        ...restOfBreakingNewsValues,
        id: breakingNews.id,
        informations: {
          ...breakingNews.informations,
          name: formValues.name,
          text: formValues.text,
          description: formValues.description,
          coverMedia: formValues.coverMedia,
          isLongText: formValues.isLongText,
        },
        scheduling: {
          startDate: formValues.startDate ? dateToString(formValues.startDate) : dateToString(new Date()),
          endDate: formValues.endDate ? dateToString(formValues.endDate) : dateToString(new Date()),
        },
        notification: {
          isRecurringNotification: formValues.isRecurringNotification,
          isNotificationActive: formValues.isNotificationActive,
          notificationTime: formatNotificationTime(formValues.notificationTime),
        },
      }),
    );

    try {
      if (isDirty) {
        await unwrapUpdateBreakingNews();
      }

      if (audienceDirty) {
        const hasAudienceChanged = watch('audienceTypeChanged');
        await handleBreakingNewsAudienceSave(formValues.audiences, hasAudienceChanged, breakingNews.audiences);
      }

      if (isDirty || audienceDirty) {
        enqueueBasicAlert(
          {
            id: 'breaking_news_edition_success',
            status: 'success',
            title: t('breaking_news:edition.update_success'),
          },
          ALERT_DURATION,
        );
      }
    } catch (error: any) {
      const errorText = getErrorText(error);

      enqueueBasicAlert(
        {
          id: 'breaking_news_edition_failed',
          status: 'error',
          title: errorText,
        },
        ALERT_DURATION,
      );
    }
  };

  const handleClickOnSave = async () => {
    // confirmation modal should be visible only if audience is changed
    // not if it's the first time the audience is saved
    if (watch('audienceDirty') && breakingNews.audiences && breakingNews.audiences.length > 0) {
      setVisibleSaveModal(true);
    } else {
      await handleSaveBreakingNews();
    }
  };

  const onBreakingNewsValidation = async () => {
    const { unwrap } = dispatch(
      updateBreakingNews({
        ...breakingNews,
        id: breakingNews.id,
        status: breakingNews.status === 'draft' ? 'live' : 'draft',
      }),
    );

    try {
      await unwrap();
      enqueueBasicAlert(
        {
          id: 'breaking_news_validation_success',
          status: 'success',
          title:
            breakingNews.status === 'draft'
              ? t('breaking_news:edition.validation_success')
              : t('breaking_news:edition.unvalidation_success'),
        },
        ALERT_DURATION,
      );
    } catch (e) {
      const errorText = getErrorText(e);
      enqueueBasicAlert(
        {
          id: 'breaking_news_edition_failed',
          status: 'error',
          title: errorText,
        },
        ALERT_DURATION,
      );
    }
  };

  return {
    handleSaveBreakingNews: handleClickOnSave,
    onBreakingNewsValidation,
    handleBreakingNewsAudienceSave,
  };
};
